import React, { useState, useEffect, useMemo } from 'react';

import { Grid, InputAdornment, TextField } from '@mui/material';
import CustomPaper from '../common/CustomPaper';
import { IndividualComponentLoader } from '../../utils/ComponentLoadingSkeleton';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import CategoryTitle from './components/CategoryTitle';
import Placeholder from './components/Placeholder';
import Title from './components/Title';

import useEmailTriggerStore, { getEmailTriggerStore } from './useEmailTriggerStore';

import { send_component_save_request, send_request } from '../../utils/Request';
import { ContentCopy } from '@mui/icons-material';

import { getCurrentTime } from '../../utils/getCurrentTime';
import { injectMetadataFields } from '../../utils/buildComponentSavePayload';

const EmailTriggerTemplate = (props) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [receiver, setReceiver] = useState('');
  const [receivingEmails, setReceivingEmails] = useState(true);
  const [incomingMessagePlaceholders, setIncomingMessagePlaceholders] = useState({});
  const [status, setStatus] = useState(null);

  const store = useMemo(() => getEmailTriggerStore(props.component_id), [props.component_id]);

  useEffect(() => {
    if (props.component_id === undefined) return;

    if (store.getState().loaded) {
      props.onComponentNameChanged(store.getState().name);
      props.onLastModifiedChanged(store.getState().lastModified);
      setReceiver(store.getState().data.receiver);
      setReceivingEmails(store.getState().data.receivingEmails);
      setIncomingMessagePlaceholders(store.getState().data.outputPlaceholders);
      setLoading(false);
      props.setChildTemplateLoaded(true);

      if (!store.getState().data.receiver || store.getState().data.receiver === 'none') {
        send_request(
          `component-service/trigger_via_email/generate_email/${props.component_id}`,
          '',
          {},
          'GET'
        ).then((res) => {
          if (res && res.data) {
            store.getState().setReceiver(res.data.receiver);
            setReceiver(res.data.receiver);
          }
        });
      }

      return;
    }

    setLoading(true);

    (async () => {
      try {
        const response = await send_request(
          `project-service/project/component/query/${props.component_id}/at-version/${props.version}`,
          '',
          {}
        );

        if (response && response.data) {
          const { components } = response.data;

          if (components) {
            store.getState().setEmailTrigger(components[0]);
            store.setState({ loaded: true });

            const storeName = store.getState().name;
            const storeLastModified = store.getState().lastModified;

            props.onComponentNameChanged(storeName);
            props.onComponentDescriptionChanged(components[0].description);
            props.onLastModifiedChanged(storeLastModified);

            if (store.getState().data.receiver === 'none') {
              try {
                const res = await send_request(
                  `component-service/trigger_via_email/generate_email/${props.component_id}`,
                  '',
                  {},
                  'GET'
                );
                if (res && res.data) {
                  store.getState().setReceiver(res.data.receiver);
                  setReceiver(res.data.receiver);
                }
              } catch (e) {
                // Email generation failed
              }
            } else {
              setReceiver(store.getState().data.receiver);
            }

            setReceivingEmails(store.getState().data.receivingEmails);
            setIncomingMessagePlaceholders(
              store.getState().data.outputPlaceholders
            );
            setLoading(false);
          }
        }

        props.setChildTemplateLoaded(true);
      } catch (err) {
        throw Error(err.message);
      }
    })();
  }, []);

  const handleSelectPlaceholder = (selected, category, id) => {
    const currentPlaceholders = JSON.parse(JSON.stringify(incomingMessagePlaceholders));
    const categoryToUpdate = currentPlaceholders[category];

    categoryToUpdate.forEach((item) => {
      if (item.id === id) {
        item.selected = !item.selected;
      }
    });

    currentPlaceholders[category] = categoryToUpdate;

    store.getState().setIncomingMessagePlaceholders(currentPlaceholders);
    setIncomingMessagePlaceholders(currentPlaceholders);
  };

  const handleKeyChange = (val, category, id) => {
    const currentPlaceholders = JSON.parse(JSON.stringify(incomingMessagePlaceholders));
    const categoryToUpdate = currentPlaceholders[category];

    categoryToUpdate.forEach((item) => {
      if (item.id === id) {
        item.key = val;
      }
    });

    currentPlaceholders[category] = categoryToUpdate;

    store.getState().setIncomingMessagePlaceholders(currentPlaceholders);
    setIncomingMessagePlaceholders(currentPlaceholders);
  };

  if (loading) return <IndividualComponentLoader />;

  return (
    <Grid>
      <CustomPaper sx={{ padding: '24px' }}>
        <p>Send emails to this email address to trigger this component</p>
        <TextField
          sx={{
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: 'var(--color-text-primary)',
            },
            minWidth: '500px',
          }}
          value={receiver}
          size={'small'}
          variant="outlined"
          disabled={true}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <CopyToClipboard text={receiver}>
                  <ContentCopy className="custom-icon-btn" />
                </CopyToClipboard>
              </InputAdornment>
            ),
          }}
        />

        <Title />
        {incomingMessagePlaceholders &&
          incomingMessagePlaceholders.content &&
          incomingMessagePlaceholders.addresses &&
          incomingMessagePlaceholders.metadata && (
            <>
              <CategoryTitle title="Email contents" />

              {incomingMessagePlaceholders.content.map((value, k) => {
                return (
                  <Placeholder
                    key={value.key}
                    placeholderKey={value.key}
                    id={value.id}
                    name={value.name}
                    selected={value.selected}
                    handleKeyChange={(event, val, id) => {
                      handleKeyChange(val, 'content', id);
                    }}
                    handleSelected={(event, selected, id) =>
                      handleSelectPlaceholder(selected, 'content', id)
                    }
                  />
                );
              })}

              <CategoryTitle title="Email addresses" />

              {incomingMessagePlaceholders.addresses.map((value, k) => {
                return (
                  <Placeholder
                    key={value.key}
                    placeholderKey={value.key}
                    id={value.id}
                    name={value.name}
                    selected={value.selected}
                    handleKeyChange={(event, val, id) => {
                      handleKeyChange(val, 'addresses', id);
                    }}
                    handleSelected={(event, selected, id) =>
                      handleSelectPlaceholder(selected, 'addresses', id)
                    }
                  />
                );
              })}

              <CategoryTitle title="Metadata" />

              {incomingMessagePlaceholders.metadata.map((value, k) => {
                return (
                  <Placeholder
                    key={value.key}
                    placeholderKey={value.key}
                    id={value.id}
                    name={value.name}
                    selected={value.selected}
                    handleKeyChange={(event, val, id) => {
                      handleKeyChange(val, 'metadata', id);
                    }}
                    handleSelected={(event, selected, id) =>
                      handleSelectPlaceholder(selected, 'metadata', id)
                    }
                  />
                );
              })}
            </>
          )}
      </CustomPaper>
    </Grid>
  );
};

export default EmailTriggerTemplate;
